// ============================================================================
// STUDIO CANETTE — VOIX DE MARQUE
//
// Les phrases qui portent la promesse vivent ici, pas dispersées dans les
// pages : la landing, la page Tarifs, l'écran d'inscription et les e-mails
// doivent raconter exactement la même chose. Un argument qu'on affine se
// corrige à un seul endroit.
//
// Registre : on parle à quelqu'un qui a déjà payé un shooting et qui a déjà vu
// un outil IA lui rendre une canette avec un faux logo. On est précis,
// technique, jamais dithyrambique.
// ============================================================================

export const BRAND = {
  name: 'Studio Canette',
  short: 'Studio Canette',
  domain: 'studiocanette.com',
  baseline: 'La photo produit sans le shooting.',
  // `parent` est le produit voisin dont Studio Canette est issu — c'est lui que
  // raconte FOOTER_NOTE. `maker` est l'atelier qui l'édite, et c'est lui qui
  // signe le bas de page : les deux ne se confondent pas.
  parent: 'SuperRayon',
  maker: 'Blackthorns Labs'
};

export const HERO = {
  eyebrow: 'Pour la filière boisson — uniquement la canette',
  title: 'Votre canette.\nVraies cotes.\nVraie photo.',
  lead: `Déposez votre gabarit à plat. On construit la canette **aux cotes exactes de votre format**, on plaque l'artwork sur la zone d'impression réelle, et l'image sort en qualité agence — sans qu'une IA ait le droit de réinventer votre étiquette.`,
  ctaPrimary: 'Générer ma première canette',
  ctaSecondary: 'Voir comment ça marche',
  proof: '5 images offertes · sans carte bancaire'
};

/** Le pitch anti-mockup. C'est l'argument qui fait signer. */
export const AGAINST_MOCKUPS = {
  title: 'Un mockup déforme. Nous, on construit.',
  lead: `Les générateurs de packaging plaquent votre visuel sur une photo de canette
  et laissent un modèle « embellir » le résultat. Vous récupérez un logo
  approximatif, un texte inventé et un format qui n'existe pas chez votre
  conditionneur. Inutilisable en communication réelle.`,
  rows: [
    {
      them: 'Votre image est déformée sur un template photo générique',
      us: 'La canette est générée aux cotes de votre format — ⌀ 53,0 × 133,4 mm pour une slim 250 ml, sertissage 200 compris'
    },
    {
      them: "Le modèle IA réécrit les textes et redessine les logos",
      us: "Votre artwork est plaqué en texture sur la géométrie : l'IA ne fait que la lumière et la matière, jamais le texte ni les logos"
    },
    {
      them: 'Une canette générique, une seule taille',
      us: '8 formats réels du marché, cotes et zone d\'impression éditables au dixième de millimètre'
    },
    {
      them: "L'angle de vue se négocie au prompt — on relance jusqu'à tomber à peu près juste",
      us: 'Vous tournez la canette à la souris : debout, inclinée, en lévitation. L\'angle affiché est l\'angle rendu, au degré près'
    },
    {
      them: 'Un outil généraliste : boîtes, sachets, bouteilles, flacons',
      us: 'Rien que la canette. C\'est pour ça que le rétreint du col est juste'
    }
  ]
};

export const STEPS = [
  {
    n: '01',
    title: 'Choisissez le format que vous produisez',
    body: 'Slim, sleek ou stubby 250 ml, standard 33, 44, 50 cl. Diamètre, hauteur et sertissage sont ceux du boîtier réel, pas une approximation.',
    accent: 'cyan'
  },
  {
    n: '02',
    title: 'Déposez votre gabarit à plat',
    body: "Le déroulé de la canette se plaque sur la zone d'impression exacte et se resserre sur le rétreint du col — exactement comme à l'impression. Raccord pivotable sur 360°.",
    accent: 'lime'
  },
  {
    n: '03',
    title: 'Cadrez comme un photographe',
    body: 'Focale de 28 à 180 mm, quatre éclairages, pose debout, inclinée ou en lévitation, condensation en gouttes réelles. Ce que vous voyez est ce qui part au modèle.',
    accent: 'violet'
  },
  {
    n: '04',
    title: 'Une image, un crédit',
    // LA DURÉE ANNONCÉE SE MESURE, elle ne se décide pas. « Quarante secondes »
    // datait d'un modèle qu'on n'appelle plus : les modèles d'image actuels
    // mettent deux à trois minutes sur une référence 3D, et le serveur leur
    // laisse 210 s (IMAGE_TIMEOUT_MS). Le studio affiche, lui, la médiane
    // réellement observée sur ce navigateur — voir src/lib/genstats.js. Si un
    // relevé fait mentir cette phrase, c'est la phrase qui bouge.
    body: "Deux minutes plus tard, une photo publicitaire. Le rendu part automatiquement dans votre bibliothèque avec son contexte : projet, format, ambiance, consigne.",
    accent: 'yellow'
  }
];

export const AUDIENCES = [
  {
    who: 'Équipes marketing & com',
    pain: 'Trois posts par semaine à alimenter, un shooting par an.',
    gain: 'Une déclinaison par parfum, par saison, par ambiance — sans rebooker de studio.',
    accent: 'pink'
  },
  {
    who: 'Brasseurs & producteurs',
    pain: "La gamme change plus vite que le budget photo.",
    gain: 'Le visuel du nouveau parfum existe avant même la première palette conditionnée.',
    accent: 'lime'
  },
  {
    who: 'Agences & studios de design',
    pain: 'Le client veut voir la canette avant de valider le BAT.',
    gain: 'Une présentation photoréaliste en rendez-vous, à partir du gabarit que vous venez de dessiner.',
    accent: 'cyan'
  },
  {
    who: 'Startups boisson',
    pain: "Pas encore de produit, déjà besoin de communiquer.",
    gain: 'Le pack de visuels de lancement, avant la première production.',
    accent: 'violet'
  }
];

/** Objections traitées frontalement — elles reviennent à chaque démo. */
export const FAQ = [
  {
    q: 'Est-ce que mon design est modifié ?',
    a: `Non, et c'est le cœur du produit. L'artwork est plaqué en texture sur la
    géométrie, à l'échelle du gabarit. Le rendu 3D part ensuite au modèle
    d'image comme référence, avec une consigne qui lui interdit explicitement
    de réécrire un texte ou de redessiner un logo. Si un détail vous gêne, le
    3D reste la vérité : vous exportez le PNG du viewport, pas la photo.`
  },
  {
    q: 'Les cotes sont-elles vraiment celles de mon conditionneur ?',
    a: `Les huit formats du catalogue reprennent les cotes normalisées des
    boîtiers (200 = 50,8 mm, 202 = 52,4 mm). Diamètre, hauteur et zone
    d'impression restent éditables au dixième de millimètre : si votre
    conditionneur a une spécificité, vous la saisissez.`
  },
  {
    q: 'Qu\'est-ce que je peux faire des images ?',
    a: `Tout : réseaux sociaux, fiche produit, sell-sheet, PLV, présentation
    investisseur. Vous en détenez les droits d'usage commercial. Sur l'offre
    Essai les exports portent un filigrane, il disparaît dès le premier
    abonnement.`
  },
  {
    q: 'Un crédit, c\'est quoi exactement ?',
    a: `Une image générée. Le travail en 3D — formats, artwork, cadrage,
    éclairage, export du viewport, téléchargement du gabarit — ne consomme
    rien. Vous ne payez que la photo. En pratique, comptez deux à trois
    générations par visuel que vous garderez : les allocations des offres
    sont dimensionnées avec ces itérations comprises.`
  },
  {
    q: 'Et si la génération échoue ?',
    a: `Le crédit vous est immédiatement recrédité. On ne facture jamais une
    image qui n'existe pas.`
  },
  {
    q: 'Vous gardez mes artworks ?',
    a: `Vos projets et vos rendus vivent dans votre espace, visibles de vous
    seul. Rien n'est utilisé pour entraîner quoi que ce soit. Vous pouvez tout
    exporter, et tout supprimer, depuis la page Compte.`
  },
  {
    q: 'Pourquoi seulement la canette ?',
    a: `Parce qu'un cylindre générique ne fait pas illusion. Le dôme concave, la
    couronne d'appui, le rétreint du col en emboutissage multi-passes, le double
    sertissage, la ligne de pré-découpe en goutte et la languette stay-on
    demandent chacun leur géométrie. On préfère une canette juste à dix
    packagings approximatifs.`
  },
  {
    q: 'Ça remplace un photographe ?',
    a: `Pour un packshot catalogue et pour alimenter des réseaux sociaux, oui.
    Pour une campagne d'affichage avec direction artistique, mise en scène et
    comédiens, non — et on ne le prétend pas.`
  }
];

/** Preuve technique : ce qui rassure un directeur artistique. */
export const TECH_PROOF = [
  'Dôme de fond concave et couronne d\'appui',
  'Rétreint du col en smootherstep, tangente verticale haut et bas',
  'Double sertissage et gorge de contre-fond',
  'Panneau bombé, rivet, ligne de pré-découpe en goutte',
  'Languette stay-on avec anneau de levée',
  'Encre directe sur alu ou réserve blanche',
  'Finition brillante, satinée ou mate',
  'Condensation en gouttes d\'eau réelles'
];

export const FOOTER_NOTE = `Studio Canette est développé par ${BRAND.parent}, le simulateur de linéaire
de la filière boisson. Ce que vous dessinez ici peut aller s'exposer là-bas, à l'épreuve d'un vrai rayon.`;

/** Messages d'erreur métier — mêmes mots partout. */
export const ERRORS = {
  insufficient_credits: "Plus de crédit sur cet espace. Rechargez ou passez à l'offre supérieure — le travail en 3D reste gratuit.",
  feature_locked: "Cette fonction demande une offre supérieure.",
  rate_limited: 'Trop de générations d\'affilée. Reprenez dans quelques secondes.',
  generation_failed: "Le modèle n'a pas rendu d'image. Votre crédit a été recrédité.",
  network: 'Connexion perdue. Rien n\'a été facturé.',
  auth_required: 'Connectez-vous pour générer une image.',
  // Les trois refus de la visionneuse de BAT (server/routes/share.js). Ils
  // s'adressent au CLIENT du studio, pas à son abonné : le ton doit dire
  // « ce lien ne marche plus », jamais « le site est en panne ».
  share_not_found: "Ce lien de partage n'existe pas.",
  share_revoked: 'Ce lien a été fermé par son auteur.',
  share_expired: 'Ce lien a expiré.'
};
